import * as React from "react";
import { Gauge, LifeBuoy, RefreshCw } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import { StatusBadge } from "../../../ui/components/StatusBadge";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import type { ListParams } from "../../../ui/api/listApi.types";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n, type TranslateValues } from "../../lib/i18n";
import { fetchGuidanceList, type AlertGuidanceStatus, type GuidanceListRow } from "../../lib/api/insights";
import { fetchDataCatalogs, type DataCatalog } from "../../lib/api/products";
import * as pageStyles from "../users/UsersListPage.css";

type T = (key: string, values?: TranslateValues) => string;
type StatusFilter = AlertGuidanceStatus | "none";
type Row = GuidanceListRow & { id: string };

const STATUS_BADGE: Record<StatusFilter, "none" | "pending" | "commissioning" | "online"> = {
  none: "none",
  draft: "pending",
  in_review: "commissioning",
  approved: "online",
};

/** Product-level catalogs only: component and shadow catalogs are never used for guidance lookup. */
export function isGuidanceCatalog(c: Pick<DataCatalog, "slug">): boolean {
  const slug = c.slug ?? "";
  return !slug.startsWith("component-") && !slug.startsWith("shadow-");
}

export function GuidanceStatusBadge({ status, t, label }: { status: StatusFilter; t: T; label?: string }) {
  return (
    <StatusBadge size="sm" variant={STATUS_BADGE[status]} label={label ?? t(`pro.system.insights.guidance.status.${status}`)} />
  );
}

function single<V extends string>(filters: Record<string, unknown> | undefined, key: string, allowed: readonly V[]): V | undefined {
  const raw = filters?.[key];
  const values = Array.isArray(raw) ? raw.map(String) : [];
  return values.length === 1 && (allowed as readonly string[]).includes(values[0]!) ? (values[0] as V) : undefined;
}

function columns(t: T, catalogs: DataCatalog[]): ColumnDefinition<Row>[] {
  return [
    {
      id: "code",
      label: t("pro.system.insights.guidance.col.code"),
      sortable: true,
      pinnedLeft: true,
      render: (row) => <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{row.code}</span>,
    },
    {
      id: "name",
      label: t("pro.system.insights.guidance.col.alert"),
      render: (row) => row.name,
    },
    {
      id: "catalog",
      label: t("pro.system.insights.guidance.col.catalog"),
      filterType: "checkbox",
      filterOptions: catalogs.map((c) => ({ value: c.id, label: c.name })),
      render: (row) => row.catalogName,
    },
    {
      id: "type",
      label: t("pro.system.insights.guidance.col.type"),
      filterType: "checkbox",
      filterOptions: [
        { value: "fault", label: t("pro.system.insights.guidance.type.fault") },
        { value: "alarm", label: t("pro.system.insights.guidance.type.alarm") },
      ],
      render: (row) => t("pro.system.insights.guidance.typeSeverity", { type: row.type, severity: row.severity }),
    },
    {
      id: "incidents",
      label: t("pro.system.insights.guidance.col.fired"),
      sortable: true,
      render: (row) => row.incidentCount90d,
    },
    {
      id: "status",
      label: t("pro.system.insights.guidance.col.status"),
      filterType: "checkbox",
      filterOptions: (["none", "draft", "in_review", "approved"] as const).map((s) => ({
        value: s,
        label: t(`pro.system.insights.guidance.status.${s}`),
      })),
      render: (row) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <GuidanceStatusBadge status={row.guidance?.status ?? "none"} t={t} />
          {row.guidance && row.guidance.status !== "approved" && row.guidance.liveVersion !== null ? (
            <GuidanceStatusBadge status="approved" t={t} label={t("pro.system.insights.guidance.liveVersion", { version: row.guidance.liveVersion })} />
          ) : null}
        </span>
      ),
    },
    {
      id: "updated",
      label: t("pro.system.insights.guidance.col.updated"),
      sortable: true,
      render: (row) =>
        row.guidance
          ? t("pro.system.insights.guidance.updatedBy", {
              when: new Date(row.guidance.updatedAt).toLocaleDateString(),
              name: row.guidance.authoredByName ?? "—",
            })
          : "—",
    },
  ];
}

export function GuidanceListPage() {
  const { t } = useI18n();
  const { accessToken, user } = useAuth();
  const navigate = useNavigate();
  const token = accessToken ?? localStorage.getItem("access_token");
  const [catalogs, setCatalogs] = React.useState<DataCatalog[] | null>(null);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!token) return;
    fetchDataCatalogs(token)
      .then((all) => setCatalogs(all.filter(isGuidanceCatalog).sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => setCatalogs([]));
  }, [token]);

  const cols = React.useMemo(() => columns(t, catalogs ?? []), [t, catalogs]);

  const fetchList = React.useCallback(
    async (params: ListParams) => {
      if (!token) return { data: [] as Row[], total: 0 };
      try {
        const sort = params.sort_by === "code" ? "code" : params.sort_by === "updated" ? "updated" : "incidents";
        const res = await fetchGuidanceList(token, {
          status: single(params.filters, "status", ["none", "draft", "in_review", "approved"] as const) ?? "any",
          catalogId: single(params.filters, "catalog", (catalogs ?? []).map((c) => c.id)),
          type: single(params.filters, "type", ["alarm", "fault"] as const),
          search: params.search,
          sort,
          page: Math.max(1, params.page || 1),
          limit: Math.max(1, params.limit || 25),
        });
        setLoadError(null);
        return { data: res.data.map((r) => ({ ...r, id: r.catalogAlertId })), total: res.total };
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : t("pro.system.insights.guidance.loadFailed"));
        return { data: [], total: 0 };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, refreshKey, catalogs, t],
  );

  const headerActions = React.useMemo(
    () => [
      {
        id: "usage",
        label: t("pro.system.insights.support.usageLink"),
        icon: <Gauge size={14} />,
        color: "neutral" as const,
        variant: "outline" as const,
        onClick: () => navigate({ to: "/system/insights" }),
      },
      {
        id: "support",
        label: t("pro.system.insights.supportLink"),
        icon: <LifeBuoy size={14} />,
        color: "neutral" as const,
        variant: "outline" as const,
        onClick: () => navigate({ to: "/system/insights/support", search: { ref: undefined } }),
      },
      {
        id: "refresh",
        label: t("pro.system.insights.refresh"),
        icon: <RefreshCw size={14} />,
        color: "neutral" as const,
        variant: "outline" as const,
        onClick: () => setRefreshKey((k) => k + 1),
      },
    ],
    [navigate, t],
  );

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeader}>
        <Text level="h1" color="text" weight="bold">
          {t("pro.system.insights.guidance.title")}
        </Text>
      </div>

      <div className={pageStyles.errorWrap}>
        <Callout variant="info">{t("pro.system.insights.guidance.intro")}</Callout>
      </div>
      {loadError ? (
        <div className={pageStyles.errorWrap}>
          <Callout variant="error">{loadError}</Callout>
        </div>
      ) : null}

      <div className={pageStyles.tableHost}>
        {catalogs ? (
          <ListView<Row>
            listId="system-insights-guidance"
            userId={user?.id ?? "anonymous"}
            columns={cols}
            fetchList={fetchList}
            contextKey={String(refreshKey)}
            fillContainer
            enabledViews={["table"]}
            defaultSort={{ sort_by: "incidents", order: "desc" }}
            searchPlaceholder={t("pro.system.insights.guidance.search")}
            emptyTitle={t("pro.system.insights.guidance.emptyTitle")}
            emptySubtitle={t("pro.system.insights.guidance.emptySubtitle")}
            onRowClick={(row) =>
              navigate({ to: "/system/insights/guidance/$catalogAlertId", params: { catalogAlertId: row.catalogAlertId } })
            }
            headerActions={headerActions}
          />
        ) : null}
      </div>
    </div>
  );
}
